import useAuth from "@/contexts/auth/auth-context";
import useMusioClient from "@/contexts/client/musio-client-context";
import type { License, LicenseError } from "@/types/api";
import { extractLicenseCodes, formatLicenseCodes, validateLicenseInputs } from "@/utils/utils";
import { useEffect, useState } from "react";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogOverlay, AlertDialogPortal, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Plus } from "lucide-react";
import { LicenseRedemptionRowSuccess } from "./license-redemption-row-success";
import { LicenseRedemptionRowFailure } from "./license-redemption-row-failure";
import { useSearchParams } from "wouter";
import { ReactComponent as OrangeExclamation } from "@/assets/icons/Exclamation-Orange.svg";
import { ReactComponent as Check } from "@/assets/icons/Check.svg";
import { NotificationRow } from "@/components/ui/notification-row";
import { LoadingIndicator } from "@/components/ui/loading-indicator";

enum DialogState {
  Input,
  Redeemed,
  Loading,
  Closed,
};

// MUS-C489-1C78-22CE-AF46

export const LICENSE_CODE_QUERY_PARAM = "license-code";

export const LicenseDialog = () => {
  const { musioApi } = useMusioClient();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [value, setValue] = useState<string>(params.getAll(LICENSE_CODE_QUERY_PARAM).join("\n"));
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.Closed);
  const [open, setOpen] = useState(false);

  // just gets all license codes and sets the dialog to open
  useEffect(() => {
    const licenseCodes = params.getAll(LICENSE_CODE_QUERY_PARAM).join("\n");
    if (licenseCodes) {
      console.log("License code params found:", licenseCodes);
      setValue(formatLicenseCodes(licenseCodes));
      setDialogState(DialogState.Input);
      setOpen(true);
    }
  }, []);

  const handleLicenseInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(formatLicenseCodes(e.target.value));
  };

  // TODO: clean this up. not sure what an idiomatic approach to this would be
  type RedeemOk = { licenses?: License[]; errors?: { licenseCode: string; error: string }[] };

  const { mutate, data, isError } =
    musioApi.useMutation("post", "/user/redeem-license-codes", {
      onSuccess: () => setDialogState(DialogState.Redeemed),
    });

  const validateLicenses = () => {
    mutate({
      body: { licenseCodes: extractLicenseCodes(value) }
    });
  };

  // for testing
  // const testLoadingDelay = () =>
  // (new Promise<void>((resolve) => {
  //   setTimeout(() => {
  //     setDialogState(DialogState.Redeemed);
  //     resolve();
  //   }, 2000);
  // }))
  //
  const handleLicenseRedeem = async () => {
    switch (dialogState) {
      case DialogState.Input:
        const isValid = validateLicenseInputs(value);
        if (!isValid) return;

        setDialogState(DialogState.Loading);

        validateLicenses();
        // for testing
        // await testLoadingDelay();
        setDialogState(DialogState.Redeemed);
        break;

      case DialogState.Redeemed:
        setDialogState(DialogState.Closed);
        setOpen(false);
        setParams("");
        window.location.reload();
        break;
    }
  };

  const handleBack = () => {
    setDialogState(DialogState.Closed);
    setOpen(false);
  };

  const ok = data as RedeemOk | undefined;
  const successfulRedemptions = ok?.licenses ?? [];
  const failedRedemptions = ok?.errors ?? [];
  // for testing 
  // const successfulRedemptions = Array.from({ length: 10 }, (_, i) => ({
  //   productName: `Product Name ${i + 1}`,
  // }));
  // const failedRedemptions = [{ licenseCode: "MUS-XXXX-XXXX-XXXX-XXXX" }];

  return (
    <AlertDialog open={open} onOpenChange={(val) => {
      setOpen(val);
      if (!val) setDialogState(DialogState.Closed);
    }}>
      <AlertDialogTrigger asChild>
        <Button
          className="w-[274px] bg-musio-blue active:bg-musio-blue/90 hover:bg-musio-blue/90"
          onClick={() => {
            setDialogState(DialogState.Input);
            setOpen(true);
          }}
        >
          <Plus /> Add Licenses
        </Button>
      </AlertDialogTrigger>
      <AlertDialogPortal>
        <AlertDialogOverlay className="AlertDialogOverlay" />
        <AlertDialogContent className="AlertDialogContent grid grid-rows-[1.5fr_8fr_0.5fr] gap-y-2 p-2 h-[90%]">
          <AlertDialogTitle
            className="AlertDialogTitle flex items-center justify-between gap-4 p-8 bg-musio-bg"
          >
            <div className="flex flex-col">
              <p className="text-[30px] font-semibold text-musio-light-lilac">Add Licenses</p>
              <p className="text-sm text-lilac">for account: {user?.email}</p>
            </div>

            <AlertDialogCancel asChild>
              <button
                onClick={handleBack}
                className="select-none px-8 h-[38px] flex items-center justify-center rounded-full font-bold bg-musio-red text-musio-light-lilac cursor-pointer"
                disabled={dialogState == DialogState.Redeemed}
              >
                <span className="text-sm">close</span>
              </button>
            </AlertDialogCancel>
          </AlertDialogTitle>

          <AlertDialogDescription className="AlertDialogDescription min-h-0 p-8 bg-musio-bg">
            {dialogState === DialogState.Loading ? (
              <div className="w-full h-full centered">
                <LoadingIndicator />
              </div>
            ) : dialogState === DialogState.Input ? (
              <textarea
                className="max-w-full w-full h-full max-h-full bg-musio-bg-light font-mono text-center align-top focus:outline-none border border-musio-bg-dark p-6 text-white resize-none placeholder:text-white/70"
                placeholder={`paste in your license codes:\nXXXX-XXXX-XXXX-XXXX\nXXXX-XXXX-XXXX-XXXX`}
                value={value}
                onChange={handleLicenseInput}
              />
            ) : isError ? (
              <NotificationRow intent="error">
                There was an error when attempting to redeem your licenses
              </NotificationRow>
            ) : (
              <div className="grid grid-rows-[4fr_1fr] h-full gap-4 min-h-0">
                <div className="min-h-0 overflow-auto">
                  <div className="w-full flex flex-col space-y-1 items-stretch font-mono text-white text-lg">
                    {successfulRedemptions?.map((license, index) => (
                      <LicenseRedemptionRowSuccess
                        key={index}
                        license={license as License}
                        className="w-full flex flex-row justify-between rounded-md px-4 py-2"
                        style={{ backgroundColor: "rgba(126, 120, 165, 0.4)" }}
                      />
                    ))}
                    {failedRedemptions?.map((license, index) => (
                      <LicenseRedemptionRowFailure
                        key={index}
                        license={license as LicenseError}
                        className="w-full flex flex-row justify-between rounded-md px-4 py-2"
                        style={{ backgroundColor: "rgba(126, 120, 165, 0.4)" }}
                      />
                    ))}
                  </div>
                </div>

                <div className="bg-musio-bg border border-musio-bg-dark p-4 space-y-2">
                  {successfulRedemptions?.length > 0 && (
                    <div className="flex flex-row gap-2 items-center">
                      <span className="inline-flex size-4 items-center justify-center shrink-0">
                        <Check className="size-4" />
                      </span>
                      <p>
                        You’ve successfully added {successfulRedemptions.length} licenses to your collection.
                      </p>
                    </div>
                  )}

                  {failedRedemptions?.length > 0 && (
                    <div className="flex flex-row gap-2 items-center">
                      <span className="inline-flex size-4 items-center justify-center shrink-0">
                        <OrangeExclamation className="size-4" />
                      </span>
                      <p>
                        Licenses you entered are invalid. Possible reasons: already redeemed, wrong code, expired.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </AlertDialogDescription>


          <AlertDialogFooter className="p-8 bg-musio-bg flex items-center justify-center">
            <Button
              className="select-none w-full max-w-[404px] h-[42px] flex items-center justify-center rounded-full font-bold bg-musio-green text-musio-light-lilac cursor-pointer disabled:opacity-50"
              onClick={handleLicenseRedeem}
              disabled={dialogState === DialogState.Loading || value.length == 0}
            >
              <span className="text-sm text-musio-mid-gray">
                {dialogState === DialogState.Redeemed ? "close" : "submit"}
              </span>
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>

      </AlertDialogPortal>
    </AlertDialog>
  );
};
